import { BodyText } from "@/components/ui";
import {
  type QuestionnaireType,
  VariableLengthQuestionnaireTypes,
} from "@/types/questionnaires";
import { format } from "date-fns";
import type * as fhirTypes from "fhir/r4";

interface QuestionnaireDetailDialogQuestionsProps {
  questionnaireResponse: fhirTypes.QuestionnaireResponse;
  totalQuestions: number;
  questionnaireId: QuestionnaireType;
}

// Helper function to format time string (HH:mm:ss) to 12-hour format
function formatTimeString(timeString: string): string {
  try {
    const [hours, minutes] = timeString.split(":").map(Number);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return timeString;
    }
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);

    return format(date, "h:mm a");
  } catch {
    return timeString;
  }
}

function getAnswerDisplayValue(
  answer: fhirTypes.QuestionnaireResponseItemAnswer,
): string {
  if (answer.valueCoding) {
    return answer.valueCoding.display ?? answer.valueCoding.code ?? "N/A";
  }

  if (answer.valueString) {
    return answer.valueString;
  }

  if (answer.valueTime) {
    return formatTimeString(answer.valueTime);
  }

  if (answer.valueInteger !== undefined) {
    return answer.valueInteger.toString();
  }

  if (answer.valueDecimal !== undefined) {
    return answer.valueDecimal.toString();
  }

  return "N/A";
}

// Helper function to check if answer has a supported value type
function hasSupportedAnswerType(
  answer: fhirTypes.QuestionnaireResponseItemAnswer,
): boolean {
  return !!(
    answer.valueCoding ||
    answer.valueString ||
    answer.valueTime ||
    answer.valueInteger !== undefined ||
    answer.valueDecimal !== undefined
  );
}

function getAnswerValue(item: fhirTypes.QuestionnaireResponseItem): string {
  if (!item.answer || item.answer.length === 0) {
    return "Did not answer";
  }

  const firstAnswer = item.answer[0];
  if (!hasSupportedAnswerType(firstAnswer)) {
    return "Did not answer";
  }

  return getAnswerDisplayValue(firstAnswer);
}

// Helper function to check if question was answered
function isQuestionAnswered(
  item: fhirTypes.QuestionnaireResponseItem,
): boolean {
  if (!item.answer || item.answer.length === 0) {
    return false;
  }

  const firstAnswer = item.answer[0];
  return hasSupportedAnswerType(firstAnswer);
}

function getQuestionsAndAnswers(
  questionnaireResponse: fhirTypes.QuestionnaireResponse,
) {
  if (!questionnaireResponse.item) return [];

  const items = questionnaireResponse.item.reduce((acc, item) => {
    // Handle nested items by flattening them
    if (item.item && item.item.length > 0) {
      const parentQuestionText = item.text || "";
      // For each sub-item, combine parent text with sub-item text if sub-item has text
      for (const subItem of item.item) {
        const subItemText = subItem.text || "";
        const combinedText =
          parentQuestionText && subItemText
            ? `${parentQuestionText} ${subItemText}`
            : subItemText || parentQuestionText;

        acc.push({
          ...subItem,
          text: combinedText,
        });
      }
    } else {
      acc.push(item);
    }
    return acc;
  }, [] as fhirTypes.QuestionnaireResponseItem[]);

  return (
    items
      // filter out items with no text or text is "Complete"
      .filter((item) => Boolean(item.text) && item.text !== "Complete")
      // additionally, filter out items with linkId containing "continue"
      .filter((item) => !item.linkId?.includes("continue"))
      .map((item) => ({
        question: item.text ?? "Question text missing",
        answer: getAnswerValue(item),
        isAnswered: isQuestionAnswered(item),
        linkId: item.linkId,
      }))
  );
}

export const QuestionnaireDetailDialogQuestions = ({
  questionnaireResponse,
  totalQuestions,
  questionnaireId,
}: QuestionnaireDetailDialogQuestionsProps) => {
  const questionsAndAnswers = getQuestionsAndAnswers(questionnaireResponse);
  const isVariableLengthQuestionnaire =
    VariableLengthQuestionnaireTypes.includes(questionnaireId);
  const questionCount = isVariableLengthQuestionnaire
    ? ""
    : `/${totalQuestions}`;

  return (
    <div className="flex-1 overflow-y-auto space-y-2">
      {questionsAndAnswers.map((qna, index) => (
        <div
          key={qna.linkId || index}
          className="flex flex-col p-2 border border-neutral-300 rounded-lg space-y-1"
        >
          <BodyText textColor="tertiary" size="sm">
            Question {index + 1}
            {questionCount}
          </BodyText>
          <BodyText bold size="sm">
            {qna.question}
          </BodyText>
          <BodyText size="sm" className={!qna.isAnswered ? "italic" : ""}>
            {qna.answer}
          </BodyText>
        </div>
      ))}
      {questionsAndAnswers.length === 0 && (
        <BodyText className="text-center text-tertiary py-4">
          No questions found in this response.
        </BodyText>
      )}
    </div>
  );
};
